import { getAccessToken } from "../../utils/auth";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "../../utils/api";
import {
  Card,
  CardContent,
  Typography,
  Grid,
  CardMedia,
  Box,
  Button,
  IconButton,
  Skeleton,
  Chip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";

const accent = "#0d47a1";
const accentLight = "rgba(13, 71, 161, 0.08)";

const RobotList = () => {
  const [robots, setRobots] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchResolvedCounts = async (robotId) => {
    try {
      const response = await axios.get(`/robotmsg-resolved-count/${robotId}`, {
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
        },
      });
      return response.data.data || { true: 0, false: 0 };
    } catch (error) {
      console.error(
        `Error fetching resolved counts for robotId ${robotId}:`,
        error
      );
      return { true: 0, false: 0 };
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const robotsResponse = await axios.get("/robots-get", {
          headers: {
            Authorization: `Bearer ${getAccessToken()}`,
          },
        });

        const robotsWithCounts = await Promise.all(
          (robotsResponse.data || []).map(async (robot) => {
            const counts = await fetchResolvedCounts(robot.robotId);
            return { ...robot, counts };
          })
        );

        setRobots(robotsWithCounts);
      } catch (error) {
        console.error("Error fetching robots:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const intervalId = setInterval(async () => {
      try {
        const updatedRobots = await Promise.all(
          robots.map(async (robot) => {
            const counts = await fetchResolvedCounts(robot.robotId);
            return { ...robot, counts };
          })
        );

        setRobots(updatedRobots);
      } catch (error) {
        console.error("Error updating robots:", error);
      }
    }, 5000);

    return () => clearInterval(intervalId);
  }, [robots]);

  const handleViewMessage = (robotId) => {
    navigate(`/robot/${robotId}`);
  };

  if (loading) {
    return (
      <Box sx={styles.pageWrap}>
        <Box sx={styles.innerWrap}>
          <Box sx={styles.headerSkeleton}>
            <Skeleton variant="circular" width={40} height={40} />
            <Skeleton variant="text" width={280} height={40} />
          </Box>
          <Grid container spacing={3}>
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Grid item key={i} xs={12} sm={6} md={4}>
                <Skeleton
                  variant="rounded"
                  height={320}
                  sx={{ borderRadius: 3 }}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={styles.pageWrap}>
      <Box sx={styles.innerWrap}>
        <Box sx={styles.header}>
          <IconButton
            onClick={() => navigate(-1)}
            size="medium"
            sx={styles.backButton}
          >
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={styles.title}>Robot message list</Typography>
            <Typography sx={styles.subtitle}>
              View messages status for each robot
            </Typography>
          </Box>
        </Box>

        {robots.length === 0 ? (
          <Box sx={styles.emptyState}>
            <SmartToyOutlinedIcon sx={styles.emptyIcon} />
            <Typography sx={styles.emptyTitle}>No robots found</Typography>
            <Typography sx={styles.emptySubtitle}>
              There are no robots available to show messages.
            </Typography>
          </Box>
        ) : robots.length === 1 ? (
          <Box sx={styles.singleCardWrap}>
            <Box sx={styles.singleCardInner}>
              <RobotMsgCard robot={robots[0]} index={0} onView={handleViewMessage} />
            </Box>
          </Box>
        ) : (
          <Grid container spacing={4}>
            {robots.map((robot, index) => (
              <Grid item key={robot.robotId || index} xs={12} md={6} lg={6}>
                <RobotMsgCard
                  robot={robot}
                  index={index}
                  onView={handleViewMessage}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    </Box>
  );
};

function MetaRow({ label, value }) {
  return (
    <Box sx={metaRowStyles.row}>
      <Typography sx={metaRowStyles.label}>{label}</Typography>
      <Typography sx={metaRowStyles.value} title={value}>
        {value || "—"}
      </Typography>
    </Box>
  );
}

const metaRowStyles = {
  row: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 1.5,
    py: 0.75,
    borderBottom: "1px solid rgba(0,0,0,0.04)",
    "&:last-of-type": { borderBottom: "none" },
  },
  label: {
    fontSize: "0.8125rem",
    color: "#64748b",
    fontWeight: 500,
    flexShrink: 0,
  },
  value: {
    fontSize: "0.875rem",
    color: "#1e293b",
    fontWeight: 500,
    overflow: "hidden",
    textOverflow: "ellipsis",
    wordBreak: "break-word",
    maxWidth: "65%",
    textAlign: "right",
  },
};

const styles = {
  pageWrap: {
    minHeight: "100vh",
    background:
      "radial-gradient(1200px 600px at 10% 0%, rgba(37, 99, 235, 0.10), rgba(255,255,255,0) 60%), radial-gradient(900px 450px at 90% 15%, rgba(16, 185, 129, 0.10), rgba(255,255,255,0) 55%), #ffffff",
    py: { xs: 2, md: 4 },
    px: { xs: 2, sm: 3, md: 4 },
    width: "100%",
    boxSizing: "border-box",
  },
  innerWrap: {
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box",
  },
  headerSkeleton: {
    display: "flex",
    alignItems: "center",
    gap: 2,
    mb: 3,
  },
  header: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 2,
    mb: 3,
  },
  backButton: {
    bgcolor: "white",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    "&:hover": {
      bgcolor: "#f5f5f5",
      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    },
  },
  title: {
    fontWeight: 700,
    color: "#1a1a2e",
    fontSize: { xs: "1.25rem", md: "1.5rem" },
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  subtitle: {
    color: "#64748b",
    fontSize: "0.875rem",
    mt: 0.5,
  },
  emptyState: {
    textAlign: "center",
    py: 8,
    px: 2,
    bgcolor: "white",
    borderRadius: 3,
    boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
  },
  emptyIcon: {
    fontSize: 56,
    color: "#cbd5e1",
    mb: 2,
  },
  emptyTitle: {
    color: "#475569",
    fontWeight: 600,
    fontSize: "1.125rem",
  },
  emptySubtitle: {
    color: "#94a3b8",
    fontSize: "0.875rem",
    mt: 1,
  },
  singleCardWrap: {
    display: "flex",
    justifyContent: "center",
    width: "100%",
  },
  singleCardInner: {
    width: "100%",
    maxWidth: 640,
  },
  card: {
    borderRadius: 3,
    overflow: "hidden",
    bgcolor: "white",
    boxShadow: "none",
    border: "1px solid rgba(0,0,0,0.06)",
    minHeight: 320,
    display: "flex",
    flexDirection: { xs: "column", sm: "row" },
  },
  cardLeft: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
  },
  cardRight: {
    position: "relative",
    width: { xs: "100%", sm: 280 },
    minHeight: { xs: 240, sm: 280 },
    flexShrink: 0,
    bgcolor: "#f1f5f9",
  },
  cardImage: {
    width: "100%",
    height: "100%",
    minHeight: 280,
    objectFit: "cover",
  },
  cardImagePlaceholder: {
    width: "100%",
    height: "100%",
    minHeight: 280,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    bgcolor: "#f1f5f9",
  },
  placeholderIcon: {
    fontSize: 72,
    color: "#cbd5e1",
  },
  statusChip: {
    position: "absolute",
    top: 12,
    right: 12,
    fontWeight: 600,
    fontSize: "0.75rem",
    "& .MuiChip-icon": {
      color: "inherit",
    },
  },
  statusChipInline: {
    mt: 1,
    fontWeight: 600,
    fontSize: "0.75rem",
    "& .MuiChip-icon": {
      color: "inherit",
    },
  },
  cardContent: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    py: 2.5,
    px: 3,
    "&:last-child": { pb: 2.5 },
  },
  robotName: {
    fontWeight: 700,
    fontSize: "1.2rem",
    color: "#1e293b",
    mb: 2,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  metaGrid: {
    flex: 1,
    minHeight: 0,
  },
  actions: {
    display: "flex",
    gap: 2,
    mt: 2,
    pt: 2,
    borderTop: "1px solid rgba(0,0,0,0.06)",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
  },
  messageButton: {
    textTransform: "none",
    color: accent,
    fontSize: "0.875rem",
    borderRadius: 999,
    border: `1px solid ${accent}`,
    bgcolor: "rgba(13, 71, 161, 0.04)",
    px: 2.5,
    height: 40,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    whiteSpace: "nowrap",
    "&:hover": {
      bgcolor: accentLight,
    },
  },
  countsWrap: {
    display: "flex",
    gap: 2.5,
    alignItems: "center",
  },
  countCircleResolved: {
    width: 40,
    height: 40,
    borderRadius: "50%",
    backgroundColor: "green",
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "1rem",
  },
  countCircleUnresolved: {
    width: 40,
    height: 40,
    borderRadius: "50%",
    backgroundColor: "red",
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "1rem",
  },
};

function RobotMsgCard({ robot, index, onView }) {
  const hasUnresolved = (robot.counts && robot.counts.false > 0) || false;

  return (
    <Card
      elevation={0}
      sx={{
        ...styles.card,
        border: hasUnresolved
          ? "2px solid rgba(211, 47, 47, 0.6)"
          : styles.card.border,
      }}
    >
      <Box sx={styles.cardLeft}>
        <CardContent sx={styles.cardContent}>
          <Typography sx={styles.robotName}>{robot.username || "—"}</Typography>
          <Box sx={styles.metaGrid}>
            <MetaRow label="Robot ID" value={robot.robotId} />
            <MetaRow label="IP" value={robot.IPAddress} />
            <MetaRow label="Model" value={robot.model} />
            <MetaRow label="Serial" value={robot.serialNumber} />
            <MetaRow label="Status" value={robot.status} />
            <MetaRow
              label="Location"
              value={`${robot.location || ""} ${robot.subLocation || ""}`.trim()}
            />
            <MetaRow
              label="Last maintenance"
              value={
                robot.lastMaintenanceDate
                  ? new Date(robot.lastMaintenanceDate).toLocaleDateString()
                  : "—"
              }
            />
          </Box>

          <Box sx={styles.actions}>
            <Button
              onClick={() => onView(robot.robotId)}
              size="small"
              sx={styles.messageButton}
              variant="text"
            >
              View messages
            </Button>
            <Box sx={styles.countsWrap}>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography sx={{ fontSize: "0.85rem", color: "#166534" }}>
                  Resolved
                </Typography>
                <Box sx={styles.countCircleResolved}>
                  {robot.counts?.true || 0}
                </Box>
              </Box>
              {hasUnresolved && (
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography sx={{ fontSize: "0.85rem", color: "#b91c1c" }}>
                    Unresolved
                  </Typography>
                  <Box sx={styles.countCircleUnresolved}>
                    {robot.counts?.false || 0}
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </CardContent>
      </Box>
      <Box sx={styles.cardRight}>
        {robot.image ? (
          <>
            <CardMedia
              component="img"
              image={robot.image}
              alt={robot.username}
              sx={styles.cardImage}
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
            <Chip
              size="small"
              label={robot.status || "—"}
              sx={{
                ...styles.statusChip,
                bgcolor: hasUnresolved ? "rgba(211, 47, 47, 0.12)" : "#e0f2fe",
                color: hasUnresolved ? "#d32f2f" : accent,
              }}
            />
          </>
        ) : (
          <Box sx={styles.cardImagePlaceholder}>
            <SmartToyOutlinedIcon sx={styles.placeholderIcon} />
            <Chip
              size="small"
              label={robot.status || "—"}
              sx={{
                ...styles.statusChip,
                bgcolor: hasUnresolved ? "rgba(211, 47, 47, 0.12)" : "#e0f2fe",
                color: hasUnresolved ? "#d32f2f" : accent,
              }}
            />
          </Box>
        )}
        <Typography
          sx={{
            position: "absolute",
            top: 8,
            left: 8,
            bgcolor: "rgba(15,23,42,0.65)",
            color: "#fff",
            px: 1,
            py: 0.5,
            borderRadius: 1,
            fontSize: "0.75rem",
          }}
        >
          #{index + 1}
        </Typography>
      </Box>
    </Card>
  );
}

export default RobotList;
